import React from 'react';
import './LegalPages.css';

const DataDeletion: React.FC = () => {
  return (
    <div className="legal-page">
      <div className="container">
        <header className="page-header">
          <h1>🗑️ Account & Data Deletion</h1>
          <p className="date">Last updated: June 13, 2026</p>
        </header>

        <div className="legal-content">
          <p className="intro">
            At Flow XP, we respect your right to delete your account and personal data at any time. This page explains how to
            delete your account, what data will be deleted, and the timeline for deletion.
          </p>

          <section className="legal-section">
            <h2>How to Delete Your Account</h2>
            <p className="highlight-text">
              ⚠️ <strong>Important:</strong> Account deletion is permanent and irreversible. All your data, progress, and
              purchases will be lost.
            </p>

            <div className="steps-box">
              <h3>From the Flow XP Mobile App:</h3>
              <ol className="steps-list">
                <li>
                  <strong>Open Flow XP</strong> on your device
                </li>
                <li>
                  <strong>Go to Settings</strong> (tap your profile icon → Settings)
                </li>
                <li>
                  <strong>Scroll down to Account section</strong>
                </li>
                <li>
                  <strong>Tap "Delete My Account"</strong>
                </li>
                <li>
                  <strong>Read the warning</strong> and confirm your choice
                </li>
                <li>
                  <strong>Enter your password</strong> to confirm deletion
                </li>
                <li>
                  <strong>Tap "Delete Permanently"</strong>
                </li>
              </ol>
            </div>

            <div className="info-box">
              <h3>Alternative: Request Deletion by Email</h3>
              <p>If you cannot access the app, you can request account deletion by email:</p>
              <ol>
                <li>
                  Send an email to <a href="mailto:flowledgeteam@gmail.com">flowledgeteam@gmail.com</a>
                </li>
                <li>Subject: "Account Deletion Request"</li>
                <li>Include: Your username and the email address associated with your account</li>
                <li>We will process your request within 48 hours</li>
              </ol>
            </div>
          </section>

          <section className="legal-section">
            <h2>What Data Will Be Deleted?</h2>
            <p>When you delete your account, the following data will be permanently deleted:</p>

            <h3>Account Information:</h3>
            <ul>
              <li>Email address</li>
              <li>Username and profile picture</li>
              <li>Authentication credentials</li>
              <li>Firebase user ID</li>
            </ul>

            <h3>Progress and Game Data:</h3>
            <ul>
              <li>Your level, class, and XP points</li>
              <li>Flow points and virtual currency</li>
              <li>Unlocked items and inventory</li>
              <li>Challenge history and streaks</li>
              <li>Statistics and achievements</li>
            </ul>

            <h3>Content:</h3>
            <ul>
              <li>Training videos you uploaded</li>
              <li>Messages and conversations</li>
              <li>Comments and likes</li>
              <li>Photos and media</li>
            </ul>

            <h3>Technical Data:</h3>
            <ul>
              <li>Device identifiers</li>
              <li>Push notification tokens</li>
              <li>Usage logs</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>Data Deletion Timeline</h2>
            <div className="timeline-box">
              <div className="timeline-item">
                <div className="timeline-icon">🔒</div>
                <div className="timeline-content">
                  <h4>Immediately</h4>
                  <p>Your account is deactivated and you can no longer access it</p>
                </div>
              </div>

              <div className="timeline-item">
                <div className="timeline-icon">🗑️</div>
                <div className="timeline-content">
                  <h4>Within 30 Days</h4>
                  <p>All your personal data is permanently deleted from our servers</p>
                </div>
              </div>

              <div className="timeline-item">
                <div className="timeline-icon">📊</div>
                <div className="timeline-content">
                  <h4>Exceptions</h4>
                  <p>
                    Some data may be retained longer for legal or security reasons (fraud prevention, legal obligations),
                    but will be anonymized
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section className="legal-section">
            <h2>What Happens to Your Purchases?</h2>
            <p className="highlight-text">
              ⚠️ <strong>Important:</strong> Purchases are non-refundable. When you delete your account:
            </p>
            <ul>
              <li>Premium subscriptions will be canceled</li>
              <li>You will lose access to all purchased content and benefits</li>
              <li>No refunds will be issued for remaining subscription time</li>
              <li>Virtual items and Flow points will be permanently lost</li>
            </ul>
            <p>
              We recommend canceling your Premium subscription in Google Play <strong>before</strong> deleting your account if
              you want to ensure no further charges.
            </p>
          </section>

          <section className="legal-section">
            <h2>Can I Recover My Account After Deletion?</h2>
            <p className="highlight-text">
              ⚠️ <strong>No.</strong> Account deletion is permanent and irreversible. Once deleted:
            </p>
            <ul>
              <li>You cannot recover your account</li>
              <li>All your data, progress, and content are permanently lost</li>
              <li>Your username may become available for others to use</li>
              <li>You can create a new account, but you'll start from scratch</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>Alternative: Temporary Deactivation</h2>
            <p>If you're unsure about permanently deleting your account, consider these alternatives:</p>
            <div className="info-box">
              <h3>Log Out</h3>
              <p>Simply log out of the app. Your account remains active and you can log back in anytime.</p>
            </div>
            <div className="info-box">
              <h3>Disable Notifications</h3>
              <p>Turn off push notifications in Settings → Notifications or in your device settings.</p>
            </div>
            <div className="info-box">
              <h3>Contact Support</h3>
              <p>
                If you're experiencing issues with the app, contact us at{' '}
                <a href="mailto:flowledgeteam@gmail.com">flowledgeteam@gmail.com</a>. We're here to help!
              </p>
            </div>
          </section>

          <section className="legal-section">
            <h2>Your Data Rights (GDPR)</h2>
            <p>Under GDPR, you also have the right to:</p>
            <ul>
              <li><strong>Access your data:</strong> Request a copy of all data we have about you</li>
              <li><strong>Rectify your data:</strong> Correct inaccurate or incomplete information</li>
              <li><strong>Restrict processing:</strong> Limit how we use your data</li>
              <li><strong>Data portability:</strong> Receive your data in a portable format</li>
            </ul>
            <p>
              To exercise these rights, contact us at:{' '}
              <a href="mailto:flowledgeteam@gmail.com">flowledgeteam@gmail.com</a>
            </p>
            <p>
              For more information, see our <a href="/privacy">Privacy Policy</a>.
            </p>
          </section>

          <section className="legal-section contact-section">
            <h2>Questions About Data Deletion?</h2>
            <div className="contact-box">
              <p>If you have questions about deleting your account or data, we're here to help:</p>
              <p><strong>Email:</strong> <a href="mailto:flowledgeteam@gmail.com">flowledgeteam@gmail.com</a></p>
              <p><strong>Response time:</strong> Usually within 24-48 hours</p>
              <p className="support-link">
                <a href="/support">Visit our Support page</a> for more help
              </p>
            </div>
          </section>

          <p className="closing-text">
            We're sorry to see you go, but we respect your decision. If you choose to return in the future, we'll be happy to
            welcome you back to the Flow XP community! 💙
          </p>
        </div>
      </div>
    </div>
  );
};

export default DataDeletion;
